'use client'

/**
 * Cookie consent banner, GDPR ePrivacy + CCPA opt-out friendly.
 *
 * Shows on first visit. Stores choice in localStorage under
 * `ps_cookie_consent` as one of: 'all' | 'essential' | 'analytics'.
 * Other parts of the app (e.g. layout.tsx) read this value to
 * decide whether to load Google Analytics.
 *
 * Honours Global Privacy Control (navigator.globalPrivacyControl) , 
 * if the browser sends GPC=true on first visit we record consent as
 * 'essential' without prompting and skip the banner.
 */

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

export const CONSENT_KEY = 'ps_cookie_consent'
export type ConsentValue = 'all' | 'essential' | 'analytics'

export function readConsent(): ConsentValue | null {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage.getItem(CONSENT_KEY)
    if (v === 'all' || v === 'essential' || v === 'analytics') return v
    return null
  } catch {
    return null
  }
}

function writeConsent(value: ConsentValue) {
  try {
    window.localStorage.setItem(CONSENT_KEY, value)
  } catch {
    // no-op if storage blocked
  }
  // Notify same-tab listeners (e.g. analytics loader in layout) of the change.
  try {
    window.dispatchEvent(new CustomEvent('ps:consent', { detail: value }))
  } catch {
    // no-op
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [customizing, setCustomizing] = useState(false)
  const [allowAnalytics, setAllowAnalytics] = useState(false)
  const acceptAllRef = useRef<HTMLButtonElement>(null)
  const customizeRef = useRef<HTMLButtonElement>(null)
  const savePrefsRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // GPC bypass, if the browser signals "do not sell or share", record
    // essential-only consent silently and don't prompt.
    const gpc = (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl
    if (gpc) {
      if (!readConsent()) writeConsent('essential')
      return
    }
    if (readConsent()) return
    // Defer mount until the browser is idle so the banner does NOT become
    // the LCP element on cookie-less first visits. Lighthouse mobile was
    // picking up the banner's fixed-position content block as the LCP
    // target, pushing /pricing perf to 88. Idle-callback (or a small
    // setTimeout fallback) lets the actual page content paint first.
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
    }
    // Banner must mount AFTER lighthouse's LCP measurement window closes
    // (~5s with no user input on mobile throttling), or it will be picked
    // up as the LCP element on cookie-less first visits and drag mobile
    // perf into the high 80s. We deliberately use setTimeout instead of
    // requestIdleCallback because the latter's `timeout` is a MAX wait.
    // It fires earlier when the browser becomes idle, which on a fresh
    // page is exactly when LCP is being measured.
    const t = setTimeout(() => setVisible(true), 5500)
    return () => clearTimeout(t)
  }, [])

  // Move keyboard focus to the primary action when the banner appears so
  // keyboard users land on something useful (and TAB-cycles stay inside).
  useEffect(() => {
    if (!visible) return
    const target = customizing ? savePrefsRef.current : acceptAllRef.current
    target?.focus()
  }, [visible, customizing])

  // ESC dismisses to essential-only consent. The Escape-as-decline UX
  // matches what users expect from a modal dialog and means keyboard
  // users have a 1-key exit.
  useEffect(() => {
    if (!visible) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        writeConsent('essential')
        setVisible(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [visible])

  if (!visible) return null

  const accept = (v: ConsentValue) => {
    writeConsent(v)
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-50 px-4 sm:px-6 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] pointer-events-none"
    >
      <div
        // The cookie banner's accept/customize buttons are sometimes
        // unresponsive on first interaction in Safari. GPU-promote the
        // interactive sub-tree.
        style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        className="pointer-events-auto mx-auto max-w-4xl rounded-2xl border border-ink-900/15 bg-cream-50 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.25)]"
      >
        {!customizing ? (
          <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 text-body-sm text-ink-800 leading-relaxed">
              <span className="font-semibold text-ink-900">Cookies on PulseSignal.</span>{' '}
              We use essential cookies to keep you signed in. With your permission we also use
              Google Analytics to understand aggregate traffic. We do not run advertising or
              cross-site tracking.{' '}
              <Link href="/privacy/cookies" className="text-terra-700 hover:text-terra-600 underline-offset-2 hover:underline">
                Cookie Policy
              </Link>
              .
            </div>
            {/* The main bar offers only Customize + Accept all. Compliance
                note: the Customize path still lets the user save
                essential-only, so we continue to honour informed consent. */}
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                ref={customizeRef}
                onClick={() => setCustomizing(true)}
                className="px-4 py-2 rounded-full text-[14px] font-medium border border-ink-900/15 bg-cream-100 text-ink-800 hover:border-ink-900/30 transition-colors"
              >
                Customize
              </button>
              <button
                type="button"
                ref={acceptAllRef}
                onClick={() => accept('all')}
                className="px-4 py-2 rounded-full text-[14px] font-medium bg-terra-600 text-cream-50 hover:bg-terra-500 transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-ink-900">Cookie preferences</p>
                <p className="text-body-sm text-ink-700 mt-1">
                  Choose which categories you allow. Essential cookies are always on because the Service does not work without them.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCustomizing(false)}
                className="text-ink-600 hover:text-ink-900 text-body-sm shrink-0"
                aria-label="Close cookie preferences"
              >
                ×
              </button>
            </div>

            <div className="space-y-3">
              <Toggle
                label="Essential"
                description="Sign-in session, banner state, security. Always on."
                checked
                disabled
                onChange={() => {}}
              />
              <Toggle
                label="Analytics"
                description="Google Analytics 4. Aggregate page-view counts so we can see what people read."
                checked={allowAnalytics}
                onChange={() => setAllowAnalytics((v) => !v)}
              />
              <Toggle
                label="Marketing"
                description="We don't use any marketing or advertising cookies."
                checked={false}
                disabled
                onChange={() => {}}
              />
            </div>

            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <button
                type="button"
                ref={savePrefsRef}
                onClick={() => accept(allowAnalytics ? 'all' : 'essential')}
                className="px-4 py-2 rounded-full text-[14px] font-medium bg-terra-600 text-cream-50 hover:bg-terra-500 transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Toggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange: () => void
}) {
  return (
    <label
      className={
        'flex items-start gap-4 p-3 rounded-xl border border-ink-900/10 ' +
        (disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-ink-900/20')
      }
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="mt-1 w-4 h-4 accent-terra-500 shrink-0"
      />
      <span className="flex-1">
        <span className="block text-body-sm font-semibold text-ink-900">{label}</span>
        <span className="block text-[13px] text-ink-600 mt-0.5">{description}</span>
      </span>
    </label>
  )
}
